import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, setImpersonation } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlanPreview } from "../context/PlanPreviewContext";
import { PageTitle, Spinner } from "../lib/ui";
import { countryName } from "../lib/format";
import Modal from "../components/Modal";
import { useI18n } from "../i18n";

const HIDDEN_PLAN_IDS = ["independent_basic", "independent_pro", "business"];
const FLAGS_T = {
  es: { title: "Feature flags (por plan y piloto)", save: "Guardar flags", saved: "Feature flags guardados", empty: "Vacío = todos los planes. Workspaces piloto (ON aunque el plan no lo incluya):" },
  en: { title: "Feature flags (by plan and pilot)", save: "Save flags", saved: "Feature flags saved", empty: "Empty = all plans. Pilot workspaces (ON even if the plan doesn't include it):" },
  fr: { title: "Feature flags (par formule et pilote)", save: "Enregistrer", saved: "Feature flags enregistrés", empty: "Vide = toutes les formules. Espaces pilotes (ON même si la formule ne l'inclut pas) :" },
  pt: { title: "Feature flags (por plano e piloto)", save: "Guardar flags", saved: "Feature flags guardados", empty: "Vazio = todos os planos. Workspaces piloto (ON mesmo que o plano não o inclua):" },
  it: { title: "Feature flags (per piano e pilota)", save: "Salva flags", saved: "Feature flags salvati", empty: "Vuoto = tutti i piani. Workspace pilota (ON anche se il piano non lo include):" },
};
const FLAG_LABELS = {
  monthly_reports: { es: "Informes mensuales (PDF/Excel/CSV)", en: "Monthly reports (PDF/Excel/CSV)", fr: "Rapports mensuels (PDF/Excel/CSV)", pt: "Relatórios mensais (PDF/Excel/CSV)", it: "Report mensili (PDF/Excel/CSV)" },
  weekly_summary: { es: "Resumen semanal por email", en: "Weekly email summary", fr: "Résumé hebdomadaire par e-mail", pt: "Resumo semanal por email", it: "Riepilogo settimanale via email" },
  advisor_view: { es: "Vista de gestor/contable (Premium)", en: "Accountant/manager view (Premium)", fr: "Vue gestionnaire/comptable (Premium)", pt: "Vista de gestor/contabilista (Premium)", it: "Vista gestore/commercialista (Premium)" },
  ocr: { es: "OCR de tickets y recibos (próximamente)", en: "OCR for tickets and receipts (coming soon)", fr: "OCR des tickets et reçus (bientôt)", pt: "OCR de tickets e recibos (brevemente)", it: "OCR di scontrini e ricevute (prossimamente)" },
};
const flagPlanChecked = (f, pid) => {
  const pl = f.plans || [];
  if (pl.includes(pid)) return true;
  if (pid === "private_basic" && pl.includes("independent_pro")) return true;
  if (pid === "premium_accountant" && pl.includes("business")) return true;
  return false;
};


const ADMIN_IDLE_MS = 15 * 60 * 1000; // sesión admin: cierre por inactividad

const STATUS_COLORS = {
  trialing: "bg-[#0299A0]/15 text-[#075E62]", active: "bg-[#2E7D32]/15 text-[#2E7D32]",
  past_due: "bg-[#B4533F]/15 text-[#B4533F]", unpaid: "bg-[#B4533F]/15 text-[#B4533F]",
  canceled: "bg-[#5F8588]/15 text-[#5F8588]", free: "bg-[#5F8588]/10 text-[#5F8588]",
};

const ROLE_LABEL = { admin: "Administrador", support: "Soporte", finance: "Finanzas", accountant: "Contable", user: "Cliente" };

export default function Admin() {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const isAdmin = role === "admin";
  const canSupport = role === "admin" || role === "support";
  const canFinance = role === "admin" || role === "finance";
  const canImpersonate = canSupport;
  const { previewPlan, setPreviewPlan } = usePlanPreview();
  const { locale } = useI18n();
  const FT = FLAGS_T[locale] || FLAGS_T.es;
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fPlan, setFPlan] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fExpiring, setFExpiring] = useState(false);
  const [fFree, setFFree] = useState(false);
  const [detail, setDetail] = useState(null);
  const [billing, setBilling] = useState(null);
  const [usageD, setUsageD] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [ops, setOps] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [prices, setPrices] = useState(null);
  const [priceStripe, setPriceStripe] = useState({ enabled: false, prices: {} });
  const [flags, setFlags] = useState(null);
  const [planOpts, setPlanOpts] = useState([]);
  const [retention, setRetention] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState("");
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupEnabled, setCleanupEnabled] = useState(false);
  const [backups, setBackups] = useState({ backups: [], last: null });
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreId, setRestoreId] = useState("");
  const [restoreRep, setRestoreRep] = useState(null);
  const [secHealth, setSecHealth] = useState(null);
  const idleRef = useRef(null);

  // Cierre por inactividad (sesión admin corta)
  useEffect(() => {
    const reset = () => { clearTimeout(idleRef.current); idleRef.current = setTimeout(() => { toast.message("Sesión admin cerrada por inactividad"); logout(); }, ADMIN_IDLE_MS); };
    const evs = ["mousemove", "keydown", "click"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { evs.forEach((e) => window.removeEventListener(e, reset)); clearTimeout(idleRef.current); };
  }, [logout]);

  const previewCleanup = async () => {
    try { const { data } = await api.get("/admin/maintenance/cleanup/preview"); setCleanup(data); if (!data.whitelist_ok) toast.error("La lista blanca NO resuelve a 2 cuentas. Revisa los datos antes de borrar."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadCleanupBackup = async () => {
    try {
      const res = await api.get("/admin/maintenance/cleanup/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a"); a.href = url; a.download = `lura_cleanup_backup_${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Backup JSON descargado");
    } catch (e) { toast.error(apiError(e)); }
  };
  const executeCleanup = async () => {
    if (cleanupConfirm !== "DELETE") { toast.error('Escribe DELETE para confirmar'); return; }
    setCleanupBusy(true);
    try { const { data } = await api.post("/admin/maintenance/cleanup/execute", { confirm: "DELETE" }); toast.success(`Limpieza completada · backup ${data.backup_id}`); setCleanup(null); setCleanupConfirm(""); loadKpis(); }
    catch (e) { toast.error(apiError(e)); } finally { setCleanupBusy(false); }
  };
  const loadCleanupStatus = useCallback(async () => {
    try { const { data } = await api.get("/admin/maintenance/cleanup/status"); setCleanupEnabled(!!data.enabled); } catch (e) { /* noop */ }
  }, []);
  const toggleCleanupTool = async () => {
    try {
      const { data } = await api.post("/admin/maintenance/cleanup/toggle", { enabled: !cleanupEnabled });
      setCleanupEnabled(!!data.enabled);
      if (!data.enabled) setCleanup(null);
      toast.success(data.enabled ? "Herramienta de limpieza ACTIVADA" : "Herramienta de limpieza desactivada");
    } catch (e) { toast.error(apiError(e)); }
  };
  const loadBackups = useCallback(async () => {
    try { const { data } = await api.get("/admin/backups"); setBackups(data); } catch (e) { /* noop */ }
  }, []);
  const loadSecHealth = useCallback(async () => {
    try { const { data } = await api.get("/admin/secrets-health"); setSecHealth(data); } catch (e) { /* noop */ }
  }, []);
  const runBackupNow = async () => {
    setBackupBusy(true);
    try { const { data } = await api.post("/admin/backups/run", {}); toast[data.status === "ok" ? "success" : "error"](data.status === "ok" ? `Backup OK · ${data.total_docs} docs` : `Fallo: ${data.error || ""}`); loadBackups(); }
    catch (e) { toast.error(apiError(e)); } finally { setBackupBusy(false); }
  };
  const restorePreview = async () => {
    if (!restoreId.trim()) { toast.error("Indica un backup id"); return; }
    try { const { data } = await api.post("/admin/backups/restore", { backup_id: restoreId.trim(), dry_run: true }); setRestoreRep(data); toast.success(`Dry-run: insertaría ${data.totals.would_insert}, ya existen ${data.totals.already_present}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const restoreExecute = async () => {
    if (!window.confirm("Restaurar insertará solo lo que falta (no borra nada). ¿Continuar?")) return;
    try { const { data } = await api.post("/admin/backups/restore", { backup_id: restoreId.trim(), dry_run: false, confirm: "RESTORE" }); setRestoreRep(data); toast.success(`Restaurados ${data.totals.would_insert} documentos`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadKpis = useCallback(async () => {
    try { const { data } = await api.get("/admin/kpis"); setKpis(data); } catch (e) { toast.error(apiError(e)); }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (fPlan) p.set("plan", fPlan);
      if (fStatus) p.set("status", fStatus);
      if (fExpiring) p.set("expiring", "true");
      if (fFree) p.set("free", "true");
      const { data } = await api.get(`/admin/customers?${p.toString()}`);
      setRows(data.customers || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, fPlan, fStatus, fExpiring, fFree]);

  useEffect(() => { loadKpis(); }, [loadKpis]);
  useEffect(() => { if (isAdmin) loadCleanupStatus(); }, [isAdmin, loadCleanupStatus]);
  useEffect(() => { if (isAdmin) { loadBackups(); loadSecHealth(); } }, [isAdmin, loadBackups, loadSecHealth]);
  useEffect(() => { const h = setTimeout(loadRows, 250); return () => clearTimeout(h); }, [loadRows]);

  const openDetail = async (wid) => {
    try {
      const { data } = await api.get(`/admin/customers/${wid}`); setDetail(data);
      setBilling(null); setUsageD(null); setNotes([]); setNoteText("");
      api.get(`/admin/billing/${wid}`).then((r) => setBilling(r.data)).catch(() => {});
      api.get(`/admin/usage/${wid}`).then((r) => setUsageD(r.data)).catch(() => {});
      if (canSupport) api.get(`/admin/customers/${wid}/notes`).then((r) => setNotes(r.data.notes || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async (wid) => {
    const txt = noteText.trim();
    if (!txt) return;
    try {
      const { data } = await api.post(`/admin/customers/${wid}/notes`, { text: txt });
      setNotes((n) => [data.note, ...n]); setNoteText("");
      toast.success("Nota añadida");
    } catch (e) { toast.error(apiError(e)); }
  };

  const createTicket = async (wid, email) => {
    const subject = window.prompt(`Nueva incidencia para ${email}\nAsunto:`);
    if (!subject) return;
    try {
      await api.post("/admin/tickets", { workspace_id: wid, subject, priority: "normal" });
      toast.success("Incidencia creada"); loadSupport();
    } catch (e) { toast.error(apiError(e)); }
  };

  const impersonate = async (wid) => {
    if (!window.confirm("Vas a entrar en la cuenta del cliente en modo SOLO LECTURA. Quedará registrado en auditoría. ¿Continuar?")) return;
    try {
      const { data } = await api.post(`/admin/customers/${wid}/impersonate`);
      setImpersonation({ token: data.token, email: data.target_email, name: data.target_name, expires: data.expires_at });
      await refreshMe();
      setDetail(null);
      navigate("/dashboard");
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadSupport = useCallback(async () => {
    if (!canSupport && !canFinance) return;
    try { const { data } = await api.get("/admin/tickets"); setTickets(data.tickets || []); } catch (e) { /* opcional */ }
  }, [canSupport, canFinance]);

  const loadStaff = useCallback(async () => {
    if (!isAdmin) return;
    try { const { data } = await api.get("/admin/staff"); setStaff(data.staff || []); } catch (e) { /* opcional */ }
  }, [isAdmin]);

  const updateTicket = async (id, body) => {
    try { await api.patch(`/admin/tickets/${id}`, body); loadSupport(); } catch (e) { toast.error(apiError(e)); }
  };
  const ticketNote = async (id) => {
    const txt = window.prompt("Nota interna:");
    if (!txt) return;
    try { await api.post(`/admin/tickets/${id}/notes`, { text: txt }); toast.success("Nota añadida"); loadSupport(); } catch (e) { toast.error(apiError(e)); }
  };
  const setRole = async (uid, newRole, email) => {
    if (!window.confirm(`¿Cambiar el rol de ${email} a ${ROLE_LABEL[newRole] || newRole}?`)) return;
    try { await api.post(`/admin/staff/${uid}/role`, { role: newRole }); toast.success("Rol actualizado"); loadStaff(); } catch (e) { toast.error(apiError(e)); }
  };

  const loadOps = useCallback(async () => {
    const calls = {
      rev: "/admin/revenue", dun: "/admin/dunning", health: "/admin/stripe/health",
      al: "/admin/alerts", an: "/admin/analytics", cp: "/admin/coupons",
    };
    const keys = Object.keys(calls);
    const res = await Promise.allSettled(keys.map((k) => api.get(calls[k])));
    const out = {};
    res.forEach((r, i) => { if (r.status === "fulfilled") out[keys[i]] = r.value.data; });
    if (Object.keys(out).length) setOps(out);
  }, []);
  const loadConfig = useCallback(async () => {
    if (!isAdmin && !canFinance) return;
    try {
      const { data } = await api.get("/admin/billing-prices");
      setPrices(data.prices); setPriceStripe({ enabled: data.stripe_enabled, prices: data.stripe_prices || {} });
    } catch (e) { /* opcional */ }
    if (isAdmin) {
      try { const { data } = await api.get("/admin/feature-flags"); setFlags(data.flags); setPlanOpts(data.plan_options || []); } catch (e) { /* opcional */ }
      try { const { data } = await api.get("/admin/retention"); setRetention(data.retention); } catch (e) { /* opcional */ }
    }
  }, [isAdmin, canFinance]);
  useEffect(() => { loadOps(); loadSupport(); loadStaff(); loadConfig(); }, [loadOps, loadSupport, loadStaff, loadConfig]);

  const setPriceField = (plan, interval, val) => setPrices((p) => ({ ...p, [plan]: { ...p[plan], [interval]: val } }));
  const savePrice = async (plan) => {
    const row = prices[plan];
    if (!window.confirm(`¿Guardar los nuevos precios de ${row.name || plan}?${priceStripe.enabled ? "\nSe creará un Price NUEVO en Stripe y el anterior se archivará (nunca se modifica)." : ""}`)) return;
    const body = { prices: [
      { plan, interval: "month", amount: Number(row.month), currency: row.currency || "chf" },
      { plan, interval: "year", amount: Number(row.year), currency: row.currency || "chf" },
    ] };
    try {
      const { data } = await api.put("/admin/billing-prices", body);
      setPrices(data.prices);
      toast.success(data.stripe_enabled ? "Precios guardados · nuevo Price creado en Stripe" : "Precios guardados");
      loadConfig();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFlag = (i, field, val) => setFlags((fs) => fs.map((f, idx) => (idx === i ? { ...f, [field]: val } : f)));
  const toggleFlagPlan = (i, planId) => setFlags((fs) => fs.map((f, idx) => {
    if (idx !== i) return f;
    const has = (f.plans || []).includes(planId);
    return { ...f, plans: has ? f.plans.filter((p) => p !== planId) : [...(f.plans || []), planId] };
  }));
  const setFlagPilots = (i, val) => setFlags((fs) => fs.map((f, idx) => (idx === i ? { ...f, pilot_workspaces: val.split(",").map((s) => s.trim()).filter(Boolean) } : f)));
  const saveFlags = async () => {
    try { const { data } = await api.put("/admin/feature-flags", { flags }); setFlags(data.flags); toast.success(FT.saved); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveRetention = async () => {
    try { const { data } = await api.put("/admin/retention", retention); setRetention(data.retention); toast.success("Política de retención guardada"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doAction = async (wid, path, body, label) => {
    if (!window.confirm(`¿Confirmas: ${label}?`)) return;
    try { await api.post(`/admin/customers/${wid}/${path}`, body || {}); toast.success(`${label} ✓ (registrado en auditoría)`); openDetail(wid); loadOps(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading && !kpis) return <Spinner />;

  const KPI = ({ label, value, testid }) => (
    <div className="bg-white border border-[#D5E7E5] rounded-lg p-4" data-testid={testid}>
      <div className="text-2xl font-bold text-[#075E62] tnum">{value}</div>
      <div className="text-xs text-[#5F8588] mt-1">{label}</div>
    </div>
  );

  return (
    <div data-testid="admin-page">
      <PageTitle title={isAdmin ? "Panel admin" : canSupport ? "Panel de soporte" : "Panel de finanzas"} subtitle={`${ROLE_LABEL[role] || role} · cartera de clientes (solo lectura de datos sensibles)`} />

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <KPI label="Clientes" value={kpis.total} testid="kpi-total" />
          <KPI label="En trial" value={kpis.in_trial} testid="kpi-trial" />
          <KPI label="De pago" value={kpis.paid} testid="kpi-paid" />
          <KPI label="Gratuitos" value={kpis.free} testid="kpi-free" />
          <KPI label="Nuevos (semana)" value={kpis.new_week} testid="kpi-week" />
          <KPI label="Nuevos (mes)" value={kpis.new_month} testid="kpi-month" />
        </div>
      )}

      {ops && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="admin-ops">
          {ops.rev && (
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Ingresos</div>
            <div className="text-2xl font-bold text-[#075E62] tnum">{ops.rev.mrr} <span className="text-sm text-[#5F8588]">CHF MRR</span></div>
            <div className="text-xs text-[#5F8588]">ARR {ops.rev.arr} · Cobrado 365d {ops.rev.paid_last_365d} CHF</div>
            {!ops.rev.stripe_enabled && <div className="text-xs text-[#C98A2B] mt-1">Stripe en test/sin catálogo live: cifras parciales.</div>}
          </div>)}
          {ops.health && (
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Salud de Stripe</div>
            <div className="text-xs text-[#5F8588]">Último evento: {ops.health.last_event_at ? ops.health.last_event_at.slice(0, 16).replace("T", " ") : "—"}</div>
            <div className="text-xs text-[#5F8588]">Eventos fallidos recientes: <span className={ops.health.failed_recent.length ? "text-[#B4533F] font-bold" : ""}>{ops.health.failed_recent.length}</span></div>
          </div>)}
          {ops.dun && (
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4" data-testid="admin-dunning">
            <div className="text-sm font-bold text-[#075E62] mb-2">Cobros fallidos ({ops.dun.count})</div>
            {ops.dun.count === 0 ? <div className="text-xs text-[#5F8588]">Ninguno.</div> :
              ops.dun.dunning.map((d) => <div key={d.workspace_id} className="text-xs text-[#B4533F]">{d.email} · {d.status}</div>)}
          </div>)}
          {ops.al && (
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Alertas</div>
            <div className="text-xs text-[#5F8588]">Trials caducan ≤7d: {ops.al.expiring_trials.length} · Picos de uso: {ops.al.usage_peaks.length} · Errores webhook: {ops.al.webhook_errors.length}</div>
          </div>)}
        </div>
      )}

      {ops?.an && (
        <div className="grid md:grid-cols-3 gap-4 mb-6" data-testid="admin-growth">
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Movimientos MRR (mes)</div>
            <div className="text-xs text-[#5F8588]">Nuevos {ops.an.mrr_moves.new} · Ampliaciones {ops.an.mrr_moves.expansion} · Bajadas {ops.an.mrr_moves.downgrade} · Cancelaciones {ops.an.mrr_moves.churn}</div>
          </div>
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4" data-testid="admin-churn">
            <div className="text-sm font-bold text-[#075E62] mb-2">Churn (mes)</div>
            <div className="text-2xl font-bold text-[#075E62] tnum">{ops.an.churn_rate_pct}%</div>
            <div className="text-xs text-[#5F8588]">De pago {ops.an.paying} · Conversiones trial {ops.an.trial_conversions_total}</div>
          </div>
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Embudo</div>
            <div className="text-xs text-[#5F8588]">Altas {ops.an.funnel.signups} → 1er movimiento {ops.an.funnel.with_movement} → factura/contrato {ops.an.funnel.with_invoice_or_contract} → de pago {ops.an.funnel.paying}</div>
          </div>
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Oportunidades de upgrade</div>
            <div className="text-xs text-[#5F8588]">{ops.an.upgrade_opportunities.length === 0 ? "Ninguna" : ops.an.upgrade_opportunities.map((u) => `${u.email} (${u.used})`).join(", ")}</div>
          </div>
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4">
            <div className="text-sm font-bold text-[#075E62] mb-2">Afiliados</div>
            <div className="text-xs text-[#5F8588]">Afiliados {ops.an.affiliates.affiliates} · Referidos {ops.an.affiliates.referrals} · Comisión {ops.an.affiliates.commission_total} (pend. {ops.an.affiliates.commission_pending})</div>
          </div>
          <div className="bg-white border border-[#D5E7E5] rounded-lg p-4" data-testid="admin-coupons">
            <div className="text-sm font-bold text-[#075E62] mb-2">Cupones Stripe ({(ops.cp?.coupons || []).length})</div>
            {!ops.cp?.stripe_enabled ? <div className="text-xs text-[#C98A2B]">Stripe no configurado.</div> : (
              <div>
                {(ops.cp.coupons || []).slice(0, 3).map((c) => <div key={c.id} className="text-xs text-[#5F8588]">{c.id}: {c.percent_off ? c.percent_off + "%" : c.amount_off + " CHF"} · {c.times_redeemed}/{c.max_redemptions || "∞"}</div>)}
                <button data-testid="create-coupon" onClick={async () => { const v = prompt("% de descuento (ej. 20)"); if (!v) return; try { await api.post("/admin/coupons", { percent_off: Number(v), duration: "once" }); toast.success("Cupón creado"); loadOps(); } catch (e) { toast.error(apiError(e)); } }} className="text-xs mt-2 px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Crear cupón %</button>
              </div>
            )}
          </div>
        </div>
      )}

      {(canSupport || canFinance) && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6" data-testid="admin-support">
          <div className="text-sm font-bold text-[#075E62] mb-2">Incidencias de soporte ({tickets.length})</div>
          {tickets.length === 0 ? <div className="text-xs text-[#5F8588]">Sin incidencias. Crea una desde la ficha de un cliente.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="tickets-table">
                <thead><tr className="text-left text-[#5F8588] border-b border-[#D5E7E5]">
                  <th className="p-2">Asunto</th><th className="p-2">Origen</th><th className="p-2">Plan</th><th className="p-2">Página</th><th className="p-2">Prioridad</th><th className="p-2">Estado</th><th className="p-2">Creada</th><th className="p-2">Nota</th>
                </tr></thead>
                <tbody>
                  {tickets.map((tk) => (
                    <tr key={tk.id} className="border-b border-[#D5E7E5]/60" data-testid={`ticket-row-${tk.id}`}>
                      <td className="p-2 text-[#075E62]">{tk.subject}{tk.notes?.length ? <span className="text-[#5F8588]"> · {tk.notes.length} nota(s)</span> : null}{tk.body ? <div className="text-[#5F8588] mt-0.5 max-w-xs truncate" title={tk.body}>{tk.body}</div> : null}</td>
                      <td className="p-2">{tk.source === "user_report" ? <span className="px-1.5 py-0.5 rounded bg-[#FDF3E3] text-[#C98A2B]" data-testid={`ticket-source-${tk.id}`}>Reporte beta</span> : <span className="text-[#5F8588]">Manual</span>}</td>
                      <td className="p-2 text-[#5F8588]">{tk.plan || "—"}</td>
                      <td className="p-2 text-[#5F8588] max-w-[180px] truncate" title={tk.page || ""}>{tk.page || "—"}</td>
                      <td className="p-2">{tk.priority}</td>
                      <td className="p-2">
                        {canSupport ? (
                          <select data-testid={`ticket-status-${tk.id}`} value={tk.status} onChange={(e) => updateTicket(tk.id, { status: e.target.value })} className="border border-[#D5E7E5] rounded px-1 py-0.5">
                            <option value="open">Abierta</option><option value="pending">Pendiente</option><option value="closed">Cerrada</option>
                          </select>
                        ) : (
                          <span data-testid={`ticket-status-${tk.id}`} className="text-[#5F8588]">{tk.status}</span>
                        )}
                      </td>
                      <td className="p-2 text-[#5F8588]">{(tk.created_at || "").slice(0, 10)}</td>
                      <td className="p-2">{canSupport ? <button data-testid={`ticket-note-${tk.id}`} onClick={() => ticketNote(tk.id)} className="underline text-[#0299A0]">+ nota</button> : <span className="text-[#5F8588]">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isAdmin && staff.length > 0 && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6" data-testid="admin-staff">
          <div className="text-sm font-bold text-[#075E62] mb-2">Equipo y roles</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="staff-table">
              <thead><tr className="text-left text-[#5F8588] border-b border-[#D5E7E5]"><th className="p-2">Miembro</th><th className="p-2">Rol</th></tr></thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.user_id} className="border-b border-[#D5E7E5]/60" data-testid={`staff-row-${s.user_id}`}>
                    <td className="p-2 text-[#075E62]">{s.name || "—"}<div className="text-[#5F8588]">{s.email}</div></td>
                    <td className="p-2">
                      <select data-testid={`staff-role-${s.user_id}`} value={s.role} onChange={(e) => setRole(s.user_id, e.target.value, s.email)} className="border border-[#D5E7E5] rounded px-1 py-0.5">
                        <option value="admin">Administrador</option><option value="support">Soporte</option><option value="finance">Finanzas</option><option value="accountant">Contable</option><option value="user">Cliente</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-[#5F8588] mt-2">Cambiar un rol cierra las sesiones activas del miembro (se revalida al volver a entrar).</div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-[#B4533F]/40 rounded-lg p-4 mb-6" data-testid="admin-cleanup">
          <div className="text-sm font-bold text-[#B4533F] mb-1">Mantenimiento · Limpieza de datos de prueba</div>
          <p className="text-xs text-[#5F8588] mb-3">Conserva solo tu cuenta admin (LUCIREFB25) y la de Daniel. Haz primero la vista previa, descarga el backup y solo entonces ejecuta. Acción con auditoría; las cuentas admin/staff NO se borran (se listan para tu aprobación).</p>
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#D5E7E5] flex-wrap">
            <button data-testid="cleanup-toggle-btn" onClick={toggleCleanupTool} className={`text-xs px-3 py-1.5 rounded font-semibold ${cleanupEnabled ? "bg-[#B4533F] text-white" : "bg-[#EAF3F1] text-[#5F8588]"}`}>{cleanupEnabled ? "Herramienta ACTIVADA — desactivar" : "Activar herramienta de limpieza"}</button>
            <span className="text-xs text-[#5F8588]" data-testid="cleanup-enabled-state">{cleanupEnabled ? "Activa (recuerda desactivarla al terminar)" : "Apagada por defecto"}</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <button data-testid="cleanup-preview-btn" disabled={!cleanupEnabled} onClick={previewCleanup} className="text-xs px-3 py-1.5 rounded border border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA] disabled:opacity-40">Vista previa (dry-run)</button>
            <button data-testid="cleanup-backup-btn" disabled={!cleanupEnabled} onClick={downloadCleanupBackup} className="text-xs px-3 py-1.5 rounded border border-[#C98A2B] text-[#C98A2B] hover:bg-[#FBF6EE] disabled:opacity-40">Descargar backup JSON</button>
          </div>
          {cleanup && (
            <div className="text-xs text-[#075E62] space-y-2" data-testid="cleanup-result">
              <div>Lista blanca: {cleanup.whitelist_ok ? <span className="text-[#2E7D32] font-semibold">OK (2 cuentas)</span> : <span className="text-[#B4533F] font-semibold">NO resuelve a 2 — no se puede ejecutar</span>}</div>
              <div><b>Se conservan</b> ({cleanup.totals.keep_accounts}): {cleanup.will_keep.map((k) => k.email).join(", ")}</div>
              {cleanup.privileged_pending_approval.length > 0 && (
                <div className="text-[#C98A2B]"><b>Admin/staff NO borrados (apruébalos aparte)</b>: {cleanup.privileged_pending_approval.map((p) => `${p.email} (${p.role})`).join(", ")}</div>
              )}
              <div><b>Se borran</b>: {cleanup.totals.delete_accounts} cuentas · códigos afiliado: {cleanup.will_delete_affiliate_codes.join(", ") || "—"}</div>
              <details><summary className="cursor-pointer text-[#5F8588]">Ver cuentas a borrar y cascada</summary>
                <div className="mt-1 max-h-48 overflow-auto">
                  <div className="mb-2">{cleanup.will_delete_accounts.map((d) => <div key={d.user_id}>{d.email} · {d.plan} · {(d.created_at || "").slice(0, 10)} · origen {d.origin}</div>)}</div>
                  <div className="text-[#5F8588]">Cascada: {Object.entries(cleanup.cascade_counts).map(([k, v]) => `${k}:${v}`).join("  ")}</div>
                </div>
              </details>
              <div className="flex items-center gap-2 pt-2 border-t border-[#D5E7E5]">
                <input data-testid="cleanup-confirm-input" value={cleanupConfirm} onChange={(e) => setCleanupConfirm(e.target.value)} placeholder='Escribe DELETE' className="border border-[#D5E7E5] rounded px-2 py-1 w-32" />
                <button data-testid="cleanup-execute-btn" disabled={!cleanupEnabled || !cleanup.whitelist_ok || cleanupConfirm !== "DELETE" || cleanupBusy} onClick={executeCleanup} className="text-xs px-3 py-1.5 rounded bg-[#B4533F] text-white disabled:opacity-40">Ejecutar limpieza</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-[#0299A0]/40 rounded-lg p-4 mb-6" data-testid="admin-plan-sim">
          <div className="text-sm font-bold text-[#075E62] mb-1">Simulación de plan (solo admin)</div>
          <p className="text-xs text-[#5F8588] mb-3">Previsualiza cómo se ve la app en cada plan. Solo cambia la visualización de TU sesión; no toca la base de datos ni a ningún cliente. Queda auditado.</p>
          <div className="flex flex-wrap gap-2 items-center">
            {["start", "pro", "premium"].map((p) => (
              <button key={p} data-testid={`plan-sim-${p}`} onClick={() => setPreviewPlan(p)} className={`text-xs px-3 py-1.5 rounded border ${previewPlan === p ? "bg-[#0299A0] text-white border-[#0299A0]" : "border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA]"}`}>{p.charAt(0).toUpperCase() + p.slice(1)}</button>
            ))}
            <button data-testid="plan-sim-reset" onClick={() => setPreviewPlan(null)} className="text-xs px-3 py-1.5 rounded border border-[#5F8588] text-[#5F8588] hover:bg-[#F3F7F7]">Volver al normal</button>
            {previewPlan && <span className="text-xs text-[#C98A2B] font-semibold" data-testid="plan-sim-active">Simulación admin activa: {previewPlan}</span>}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="bg-white border border-[#2E7D32]/40 rounded-lg p-4 mb-6" data-testid="admin-backups">
          <div className="text-sm font-bold text-[#075E62] mb-1">Copias de seguridad (object storage, fuera de Mongo)</div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button data-testid="backup-run-now" disabled={backupBusy} onClick={runBackupNow} className="text-xs px-3 py-1.5 rounded bg-[#2E7D32] text-white disabled:opacity-40">{backupBusy ? "Creando…" : "Hacer backup ahora"}</button>
            {backups.last ? (
              <span className="text-xs" data-testid="backup-last">Último: <b className={backups.last.status === "ok" ? "text-[#2E7D32]" : "text-[#B4533F]"}>{backups.last.status}</b> · {(backups.last.created_at || "").slice(0, 19).replace("T", " ")} · {Math.round((backups.last.size || 0) / 1024)} KB · {backups.last.total_docs ?? "—"} docs</span>
            ) : <span className="text-xs text-[#5F8588]" data-testid="backup-last">Sin backups todavía</span>}
          </div>
          {secHealth && (
            <div className="text-xs text-[#5F8588] mb-3" data-testid="secrets-health">
              Secrets: TOTP_ENCRYPTION_KEY(Fernet) {secHealth.totp_encryption_key_valid_fernet ? "✓" : "✗"} · TOTP_PEPPER {secHealth.totp_pepper_present ? "✓" : "✗"} · WEBHOOK_CRON_SECRET {secHealth.webhook_cron_secret_present ? "✓" : "✗"}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#D5E7E5]">
            <input data-testid="restore-id-input" value={restoreId} onChange={(e) => setRestoreId(e.target.value)} placeholder="backup id (bkp_…)" className="border border-[#D5E7E5] rounded px-2 py-1 text-xs w-56" />
            <button data-testid="restore-preview-btn" onClick={restorePreview} className="text-xs px-3 py-1.5 rounded border border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA]">Dry-run restaurar</button>
            <button data-testid="restore-execute-btn" disabled={!restoreRep} onClick={restoreExecute} className="text-xs px-3 py-1.5 rounded bg-[#C98A2B] text-white disabled:opacity-40">Restaurar (no borra)</button>
            {restoreRep && <span className="text-xs text-[#075E62]" data-testid="restore-report">insertaría {restoreRep.totals.would_insert} · existen {restoreRep.totals.already_present}</span>}
          </div>
        </div>
      )}

      {(isAdmin || canFinance) && prices && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6" data-testid="admin-prices">
          <div className="text-sm font-bold text-[#075E62] mb-1">Precios de planes</div>
          <div className="text-xs text-[#5F8588] mb-3">
            {priceStripe.enabled
              ? "Al guardar se crea un Price NUEVO en Stripe y se archiva el anterior (nunca se modifica un precio existente)."
              : "Stripe no activo: los precios se guardan en la configuración (se sincronizarán con Stripe al activarlo)."}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="prices-table">
              <thead><tr className="text-left text-[#5F8588] border-b border-[#D5E7E5]">
                <th className="p-2">Plan</th><th className="p-2">Mensual</th><th className="p-2">Anual</th><th className="p-2">Divisa</th><th className="p-2"></th>
              </tr></thead>
              <tbody>
                {["start", "pro", "premium"].filter((pl) => prices[pl]).map((pl) => (
                  <tr key={pl} className="border-b border-[#D5E7E5]/60" data-testid={`price-row-${pl}`}>
                    <td className="p-2 text-[#075E62] font-medium">{prices[pl].name || pl}</td>
                    <td className="p-2"><input data-testid={`price-${pl}-month`} type="number" step="0.01" min="0" disabled={!isAdmin} value={prices[pl].month ?? ""} onChange={(e) => setPriceField(pl, "month", e.target.value)} className="border border-[#D5E7E5] rounded px-2 py-1 w-24 disabled:bg-[#F2F7F6]" /></td>
                    <td className="p-2"><input data-testid={`price-${pl}-year`} type="number" step="0.01" min="0" disabled={!isAdmin} value={prices[pl].year ?? ""} onChange={(e) => setPriceField(pl, "year", e.target.value)} className="border border-[#D5E7E5] rounded px-2 py-1 w-24 disabled:bg-[#F2F7F6]" /></td>
                    <td className="p-2 text-[#5F8588]">{(prices[pl].currency || "CHF").toUpperCase()}</td>
                    <td className="p-2">{isAdmin && <button data-testid={`price-save-${pl}`} onClick={() => savePrice(pl)} className="px-3 py-1 rounded border border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA]">Guardar</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAdmin && flags && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6" data-testid="admin-flags">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-[#075E62]">{FT.title}</div>
            <button data-testid="flags-save" onClick={saveFlags} className="text-xs px-3 py-1.5 rounded border border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA]">{FT.save}</button>
          </div>
          <div className="space-y-3">
            {flags.map((f, i) => (
              <div key={f.key} className="border border-[#D5E7E5] rounded p-3" data-testid={`flag-${f.key}`}>
                <label className="flex items-center gap-2 text-sm text-[#075E62] font-medium">
                  <input type="checkbox" data-testid={`flag-enabled-${f.key}`} checked={!!f.enabled} onChange={(e) => toggleFlag(i, "enabled", e.target.checked)} />
                  {(FLAG_LABELS[f.key] && FLAG_LABELS[f.key][locale]) || f.label || f.key} <span className="text-xs text-[#5F8588] font-normal">({f.key})</span>
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {planOpts.filter((p) => !HIDDEN_PLAN_IDS.includes(p.id)).map((p) => (
                    <label key={p.id} className="text-xs flex items-center gap-1 text-[#5F8588]">
                      <input type="checkbox" data-testid={`flag-${f.key}-plan-${p.id}`} checked={flagPlanChecked(f, p.id)} onChange={() => toggleFlagPlan(i, p.id)} /> {p.name}
                    </label>
                  ))}
                </div>
                <div className="mt-2 text-xs text-[#5F8588]">
                  {FT.empty}
                  <input data-testid={`flag-pilots-${f.key}`} defaultValue={(f.pilot_workspaces || []).join(", ")} onBlur={(e) => setFlagPilots(i, e.target.value)} placeholder="ws_abc, ws_def" className="ml-2 border border-[#D5E7E5] rounded px-2 py-1 w-64" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isAdmin && retention && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6" data-testid="admin-retention">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-[#075E62]">Retención de datos (RGPD)</div>
            <button data-testid="retention-save" onClick={saveRetention} className="text-xs px-3 py-1.5 rounded border border-[#0299A0] text-[#0299A0] hover:bg-[#F0FAFA]">Guardar</button>
          </div>
          <p className="text-xs text-[#5F8588] mb-3">Propuesta a validar con un especialista legal. Datos de facturación se conservan por obligación legal suiza; los movimientos NO se conservan tras el plazo de borrado.</p>
          <div className="flex flex-wrap gap-4">
            <label className="text-xs text-[#5F8588]">Borrar datos tras cancelar (días)
              <input type="number" min="0" data-testid="retention-delete-days" value={retention.delete_after_cancel_days} onChange={(e) => setRetention({ ...retention, delete_after_cancel_days: Number(e.target.value) })} className="block mt-1 border border-[#D5E7E5] rounded px-2 py-1 w-28" /></label>
            <label className="text-xs text-[#5F8588]">Facturación (años, solo facturación)
              <input type="number" min="0" data-testid="retention-billing-years" value={retention.billing_retention_years} onChange={(e) => setRetention({ ...retention, billing_retention_years: Number(e.target.value) })} className="block mt-1 border border-[#D5E7E5] rounded px-2 py-1 w-28" /></label>
            <label className="text-xs text-[#5F8588]">Auditoría (meses)
              <input type="number" min="0" data-testid="retention-audit-months" value={retention.audit_retention_months} onChange={(e) => setRetention({ ...retention, audit_retention_months: Number(e.target.value) })} className="block mt-1 border border-[#D5E7E5] rounded px-2 py-1 w-28" /></label>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o email"
          className="border border-[#D5E7E5] rounded px-3 py-2 text-sm flex-1 min-w-[200px]" />
        <select data-testid="admin-filter-plan" value={fPlan} onChange={(e) => setFPlan(e.target.value)} className="border border-[#D5E7E5] rounded px-3 py-2 text-sm">
          <option value="">Todos los planes</option><option value="free">Free</option><option value="start">Start</option><option value="pro">Pro</option><option value="premium">Premium</option>
        </select>
        <select data-testid="admin-filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border border-[#D5E7E5] rounded px-3 py-2 text-sm">
          <option value="">Todos los estados</option><option value="trialing">Trial</option><option value="active">Activo</option><option value="past_due">Impago</option><option value="canceled">Cancelado</option>
        </select>
        <label className="text-sm flex items-center gap-1 text-[#075E62]"><input type="checkbox" data-testid="admin-filter-expiring" checked={fExpiring} onChange={(e) => setFExpiring(e.target.checked)} /> Caducan en 7 días</label>
        <label className="text-sm flex items-center gap-1 text-[#075E62]"><input type="checkbox" data-testid="admin-filter-free" checked={fFree} onChange={(e) => setFFree(e.target.checked)} /> Solo gratuitos</label>
      </div>

      <div className="bg-white border border-[#D5E7E5] rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-customers-table">
          <thead><tr className="text-left text-xs text-[#5F8588] border-b border-[#D5E7E5]">
            <th className="p-3">Cliente</th><th className="p-3">País / Idioma</th><th className="p-3">Plan</th><th className="p-3">Estado</th><th className="p-3">Trial / días</th><th className="p-3">Alta</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.workspace_id} className="border-b border-[#D5E7E5]/60 hover:bg-[#F7FBFA] cursor-pointer" data-testid={`admin-row-${r.workspace_id}`} onClick={() => openDetail(r.workspace_id)}>
                <td className="p-3"><div className="font-medium text-[#075E62]">{r.name || "—"}</div><div className="text-xs text-[#5F8588]">{r.email}</div></td>
                <td className="p-3 text-[#5F8588]">{r.country ? countryName(r.country, locale) : "—"} / {(r.locale || "—")}</td>
                <td className="p-3"><span className="font-medium text-[#075E62]">{r.plan_label}</span></td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status] || "bg-[#5F8588]/10 text-[#5F8588]"}`}>{r.status}</span></td>
                <td className="p-3 text-[#5F8588]">{r.in_trial ? `${r.days_left}d` : "—"}</td>
                <td className="p-3 text-[#5F8588] text-xs">{(r.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#5F8588]">Sin resultados</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Ficha de cliente">
        {detail && (
          <div className="space-y-4 text-sm" data-testid="admin-detail">
            <div>
              <div className="font-bold text-[#075E62] text-base">{detail.customer.name}</div>
              <div className="text-[#5F8588]">{detail.customer.email}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[#075E62]">
              <div><span className="text-[#5F8588]">Plan:</span> {detail.customer.plan_label}</div>
              <div><span className="text-[#5F8588]">Estado:</span> {detail.customer.status}</div>
              <div><span className="text-[#5F8588]">Intervalo:</span> {detail.customer.interval}</div>
              <div><span className="text-[#5F8588]">Origen:</span> {detail.customer.origin}</div>
              <div><span className="text-[#5F8588]">País/Idioma:</span> {detail.customer.country || "—"} / {detail.customer.locale || "—"}</div>
              <div><span className="text-[#5F8588]">Fin trial:</span> {(detail.customer.trial_end || "—").slice(0, 10)}</div>
              <div><span className="text-[#5F8588]">Alta:</span> {(detail.customer.created_at || "—").slice(0, 10)}</div>
              <div><span className="text-[#5F8588]">Último acceso:</span> {(detail.customer.last_login || "—").slice(0, 10)}</div>
            </div>
            {detail.customer.stripe_link && (
              <a href={detail.customer.stripe_link} target="_blank" rel="noreferrer" data-testid="admin-stripe-link" className="text-[#0299A0] underline text-sm">Ver cliente en Stripe →</a>
            )}

            {usageD && (
              <div data-testid="admin-usage">
                <div className="font-medium text-[#075E62] mb-1">Uso del mes</div>
                {usageD.unlimited ? <div className="text-xs text-[#5F8588]">Movimientos ilimitados (Pro/Premium/trial).</div> :
                  <div className={`text-xs ${usageD.warn80 ? "text-[#C98A2B]" : "text-[#5F8588]"}`}>{usageD.movements} / {usageD.limit} movimientos ({usageD.pct}%){usageD.warn80 ? " · aviso 80%" : ""}</div>}
              </div>
            )}

            {billing && (
              <div data-testid="admin-billing">
                <div className="font-medium text-[#075E62] mb-1">Facturación (Stripe)</div>
                {!billing.stripe_enabled ? <div className="text-xs text-[#C98A2B]">Stripe no configurado en este entorno.</div> :
                  billing.invoices.length === 0 ? <div className="text-xs text-[#5F8588]">Sin facturas.</div> :
                  <ul className="text-xs text-[#5F8588] space-y-1">
                    {billing.invoices.map((i) => (
                      <li key={i.id}>{i.amount.toFixed(2)} {(i.currency || "").toUpperCase()} · {i.status} · IVA {i.tax.toFixed(2)} {i.link && <a href={i.link} target="_blank" rel="noreferrer" className="text-[#0299A0] underline">factura</a>}</li>
                    ))}
                  </ul>}
              </div>
            )}

            {canSupport && (
              <div className="border-t border-[#D5E7E5] pt-3" data-testid="admin-support-actions">
                <div className="flex flex-wrap gap-2 mb-3">
                  <button data-testid="impersonate-button" onClick={() => impersonate(detail.customer.workspace_id)} className="text-xs px-3 py-1.5 rounded border border-[#C98A2B] text-[#C98A2B] hover:bg-[#FBF6EE]">Entrar como (solo lectura)</button>
                  <button data-testid="create-ticket-button" onClick={() => createTicket(detail.customer.workspace_id, detail.customer.email)} className="text-xs px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Crear incidencia</button>
                </div>
                <div className="font-medium text-[#075E62] mb-1">Notas internas</div>
                <div className="flex gap-2 mb-2">
                  <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Añadir nota interna…" className="border border-[#D5E7E5] rounded px-2 py-1 text-xs flex-1" />
                  <button data-testid="note-add-button" onClick={() => addNote(detail.customer.workspace_id)} className="text-xs px-3 py-1 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Añadir</button>
                </div>
                {notes.length === 0 ? <div className="text-xs text-[#5F8588]">Sin notas.</div> : (
                  <ul className="text-xs text-[#5F8588] space-y-1" data-testid="notes-list">
                    {notes.map((n) => <li key={n.id}><span className="text-[#075E62]">{n.text}</span> <span className="opacity-70">· {n.author} · {(n.created_at || "").slice(0, 10)}</span></li>)}
                  </ul>
                )}
              </div>
            )}

            {isAdmin && (
            <div className="border-t border-[#D5E7E5] pt-3">
              <div className="font-medium text-[#075E62] mb-2">Acciones (con confirmación y auditoría)</div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="act-extend-trial" onClick={() => doAction(detail.customer.workspace_id, "extend-trial", { days: 7 }, "Extender trial +7 días")} className="text-xs px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Extender trial +7d</button>
                <button data-testid="act-pause" onClick={() => doAction(detail.customer.workspace_id, "pause", {}, "Pausar cobro")} className="text-xs px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Pausar</button>
                <button data-testid="act-resume" onClick={() => doAction(detail.customer.workspace_id, "resume", {}, "Reactivar cobro")} className="text-xs px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Reactivar</button>
                <button data-testid="act-welcome" onClick={() => doAction(detail.customer.workspace_id, "resend-welcome", {}, "Reenviar email de bienvenida")} className="text-xs px-3 py-1.5 rounded border border-[#D5E7E5] text-[#075E62] hover:bg-[#F7FBFA]">Reenviar bienvenida</button>
                <select data-testid="act-change-plan" onChange={(e) => { if (e.target.value) doAction(detail.customer.workspace_id, "change-plan", { plan: e.target.value, interval: "month" }, `Cambiar a ${e.target.value} (vía Stripe)`); e.target.value = ""; }} className="text-xs px-2 py-1.5 rounded border border-[#D5E7E5] text-[#075E62]">
                  <option value="">Cambiar plan vía Stripe…</option><option value="start">Start</option><option value="pro">Pro</option><option value="premium">Premium</option>
                </select>
              </div>
            </div>
            )}

            <div>
              <div className="font-medium text-[#075E62] mb-1">Histórico de planes</div>
              {detail.plan_history.length === 0 ? <div className="text-[#5F8588] text-xs">Sin cambios registrados.</div> : (
                <ul className="text-xs text-[#5F8588] space-y-1">
                  {detail.plan_history.map((h) => (
                    <li key={h.id}>{(h.timestamp || "").slice(0, 10)}: {h.from} → {h.to} <span className="opacity-70">({h.reason})</span></li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
